import { Dispatch, SetStateAction } from 'react';
import { CellChange, CellStyle, Column, NumberCell, Row, TextCell } from '@silevis/reactgrid';
import { MyActionsCell } from 'src/entity/CellTemplates/ActionsCellTemplate';
import { MyDateCell } from 'src/entity/CellTemplates/DateCellTemplate';
import { DropdownCell } from 'src/entity/CellTemplates/DropDownCellTemplate';
import { MyHeaderCell } from 'src/entity/CellTemplates/HeaderCellComponent';
import { FieldType } from 'src/entity/ProjectSettingsForm/types';
import { ResolvedTableAppearance } from 'src/feature/Table/utils/appearance';
import {
  FormTotalsRowOperation,
  FormViewDependencyOperator,
  FormViewDependencyRule,
  FormViewSettings,
  TableSettingsForm,
} from 'src/shared/store/projects/types';
import { ListItem } from 'src/shared/store/table';
import { isProtectedBusinessRuleColumn } from 'src/shared/store/table/protectedColumns';
import { SortItem } from 'src/shared/store/table/types';

import { cellHeight, columnNames, headerHeight } from './constants';

export const getStyle = (props: CellStyle, borderColor = '#EFEFEF'): CellStyle => ({
  border: {
    right: { color: borderColor },
    top: { color: borderColor },
    left: { color: borderColor },
    bottom: { color: borderColor },
  },
  ...props,
});

export const getColumns = (header: FieldType[]): Column[] =>
  header.map(({ columnName, columnType }) => ({
    columnId: columnName,
    width: columnName.toLowerCase() === 'id' ? 40 : columnType === 'actions' ? 88 : undefined,
    resizable: !(columnName.toLowerCase() === 'id' || columnType === 'actions'),
  }));

export const canHideIdColumn = (header: FieldType[]): boolean =>
  header.some(({ columnName }) => columnName.toLowerCase() !== 'id');

export const getResponsiveColumnWidth = (
  containerWidth: number,
  header: FieldType[],
): number | null => {
  const flexibleColumnsCount = header.filter(
    ({ columnName, columnType }) => columnName.toLowerCase() !== 'id' && columnType !== 'actions',
  ).length;

  if (!flexibleColumnsCount) {
    return null;
  }

  const fixedColumnsWidth = header.reduce((width, { columnName, columnType }) => {
    if (columnName.toLowerCase() === 'id') {
      return width + 47;
    }

    if (columnType === 'actions') {
      return width + 88;
    }

    return width;
  }, 0);

  const calculatedWidth = Math.max(containerWidth - fixedColumnsWidth, 0) / flexibleColumnsCount - 1;

  return calculatedWidth < 200 ? 200 : calculatedWidth;
};

export const getHeaderRow = (
  tableSettings: TableSettingsForm,
  appearance?: ResolvedTableAppearance,
): Row<MyHeaderCell> => ({
  rowId: 'header',
  cells:
    tableSettings?.formStructure.map(({ columnName, columnLabel, columnType }) => ({
      type: 'header',
      text: columnName.toLowerCase() === 'id' ? 'ID' : columnLabel,
      style: getStyle(
        {
          background: appearance?.headerBackgroundColor || '#FFFFFF',
          color: appearance?.headerTextColor || appearance?.textColor || '#1E2A5A',
        },
        appearance?.borderColor || '#EFEFEF',
      ),
      enableSort: columnName.toLowerCase() !== 'id' && columnType !== 'actions',
      enableFilter: columnName.toLowerCase() !== 'id' && columnType !== 'actions',
      enableActions: columnType === 'actions',
      label: columnNames?.[columnType] || '',
    })) || [],
  height: headerHeight,
});

const normalizeDependencyKey = (value?: string): string => (value || '').trim().toLowerCase();

const getItemValueByKey = (item: ListItem, normalizedKey: string): unknown => {
  if (!normalizedKey) {
    return undefined;
  }
  if (Object.prototype.hasOwnProperty.call(item, normalizedKey)) {
    return item[normalizedKey];
  }

  const resolvedKey = Object.keys(item).find(key => normalizeDependencyKey(key) === normalizedKey);
  if (!resolvedKey) {
    return undefined;
  }
  return item[resolvedKey];
};

const isEmptyDependencyValue = (value: unknown): boolean =>
  value === null || value === undefined || value === '';

const normalizeDependencyValue = (value: unknown): string => {
  if (value === null || value === undefined) {
    return '';
  }
  if (typeof value === 'boolean') {
    return value ? 'true' : 'false';
  }
  if (typeof value === 'number') {
    return Number.isFinite(value) ? String(value) : '';
  }
  return String(value).trim();
};

const resolveDependencyRuleOperator = (
  operator?: FormViewDependencyOperator,
  values?: Array<string | number | boolean>,
): FormViewDependencyOperator => {
  if (operator) {
    return operator;
  }
  return (values?.length || 0) > 1 ? 'in' : 'eq';
};

const doesDependencyRuleMatch = (item: ListItem, rule: FormViewDependencyRule): boolean => {
  const sourceKey = normalizeDependencyKey(rule.sourceColumn);
  if (!sourceKey) {
    return true;
  }

  const sourceValue = getItemValueByKey(item, sourceKey);
  const normalizedSource = normalizeDependencyValue(sourceValue);
  const isEmptySource = isEmptyDependencyValue(sourceValue);
  const values = (rule.values || []).map(value => normalizeDependencyValue(value));
  const operator = resolveDependencyRuleOperator(rule.operator, rule.values);

  switch (operator) {
    case 'is_empty':
      return isEmptySource;
    case 'not_empty':
      return !isEmptySource;
    case 'neq':
      return values.length > 0 ? normalizedSource !== values[0] : true;
    case 'in':
      return values.length > 0 ? values.includes(normalizedSource) : false;
    case 'not_in':
      return values.length > 0 ? !values.includes(normalizedSource) : true;
    case 'eq':
    default:
      return values.length > 0 ? normalizedSource === values[0] : false;
  }
};

const shouldHideByDependentRules = (
  item: ListItem,
  columnName: string,
  viewSettings?: FormViewSettings,
): boolean => {
  const columnKey = normalizeDependencyKey(columnName);
  if (!columnKey) {
    return false;
  }

  const rules = (viewSettings?.dependentDisplayRules || []).filter(
    rule =>
      normalizeDependencyKey(rule.targetColumn) === columnKey &&
      rule.enabled !== false &&
      normalizeDependencyKey(rule.sourceColumn),
  );

  if (!rules.length) {
    return false;
  }

  return !rules.every(rule => doesDependencyRuleMatch(item, rule));
};

const parseNumericTotalsValue = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string') {
    const normalized = value.replace(',', '.').trim();
    if (!normalized) {
      return null;
    }
    const parsed = Number(normalized);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

const isNonEmptyTotalsValue = (value: unknown): boolean =>
  !(value === null || value === undefined || value === '');

const calculateTotalsValue = (
  list: ListItem[],
  columnName: string,
  operation: FormTotalsRowOperation,
): number | null => {
  const rawValues = list.map(item => item[columnName]);
  const numericValues = rawValues
    .map(value => parseNumericTotalsValue(value))
    .filter((value): value is number => value !== null);

  switch (operation) {
    case 'sum':
      return numericValues.reduce((acc, value) => acc + value, 0);
    case 'avg':
      return numericValues.length
        ? numericValues.reduce((acc, value) => acc + value, 0) / numericValues.length
        : null;
    case 'count':
      return numericValues.length;
    case 'counta':
      return rawValues.filter(isNonEmptyTotalsValue).length;
    case 'max':
      return numericValues.length ? Math.max(...numericValues) : null;
    case 'min':
      return numericValues.length ? Math.min(...numericValues) : null;
    case 'product':
      return numericValues.length ? numericValues.reduce((acc, value) => acc * value, 1) : null;
    default:
      return null;
  }
};

const buildTotalsRow = (
  list: ListItem[],
  header: FieldType[],
  viewSettings?: FormViewSettings,
  appearance?: ResolvedTableAppearance,
): Row<any> | null => {
  const totalsRow = viewSettings?.totalsRow;
  if (!totalsRow?.enabled || !totalsRow.columns?.length) {
    return null;
  }

  const operationsByColumn = totalsRow.columns.reduce<Record<string, FormTotalsRowOperation>>(
    (acc, column) => {
      if (column.columnName?.trim() && column.operation) {
        acc[column.columnName.trim()] = column.operation;
      }
      return acc;
    },
    {},
  );

  if (!Object.keys(operationsByColumn).length) {
    return null;
  }

  const labelColumnIndex = header.findIndex(
    column => column.columnType !== 'actions' && column.columnName.toLowerCase() !== 'id',
  );
  const style = getStyle(
    {
      background: appearance?.headerBackgroundColor || appearance?.backgroundColor || '#F6F7FB',
      color: appearance?.headerTextColor || appearance?.textColor || '#1E2A5A',
    },
    appearance?.borderColor || '#EFEFEF',
  );
  const label = totalsRow.label?.trim() || 'Итоги';

  return {
    rowId: totalsRow.position === 'start' ? 'totals-row-start' : 'totals-row-end',
    height: cellHeight,
    cells: header.map((column, index) => {
      if (column.columnType === 'actions') {
        return {
          type: 'actions',
          isEdit: false,
          style,
          nonEditable: true,
        };
      }

      const configuredOperation = operationsByColumn[column.columnName];
      if (configuredOperation) {
        const value = calculateTotalsValue(list, column.columnName, configuredOperation);
        if (column.columnType === 'number' || column.columnType === 'floatNumber') {
          return {
            type: column.columnType,
            value,
            style,
            nonEditable: true,
          };
        }

        return {
          type: 'text',
          text: value === null ? '' : String(value),
          style,
          nonEditable: true,
        };
      }

      if (index === labelColumnIndex) {
        return {
          type: 'text',
          text: label,
          style,
          nonEditable: true,
        };
      }

      return {
        type: 'text',
        text: '',
        style,
        nonEditable: true,
      };
    }),
  };
};

export const getRows = (
  list: ListItem[],
  headerRow: Row<MyHeaderCell>,
  header: FieldType[],
  isEdit: boolean,
  viewSettings?: FormViewSettings,
  appearance?: ResolvedTableAppearance,
  protectedRuleColumns?: Set<string>,
): Row[] => {
  const bodyRows = list.map<Row<any>>((item, i) => ({
    rowId: i,
    cells: header.map(head => {
      const type = head.columnType;
      const name = head.columnName;
      const value = item[head.columnName];
      const isEmptyValue = value === null || value === undefined || value === '';
      const isReferenceValue = type === 'dropdown' || type === 'reference';
      const shouldHideEmpty = Boolean(viewSettings?.hideEmptyCells && isEmptyValue);
      const shouldHideReference = Boolean(viewSettings?.hideReferencedValues && isReferenceValue);
      const shouldHideDependent = shouldHideByDependentRules(item, name, viewSettings);
      const isRuleProtected = isProtectedBusinessRuleColumn(name, protectedRuleColumns);
      const shouldHideValue = shouldHideEmpty || shouldHideReference || shouldHideDependent;

      const cell = {
        type,
        style: getStyle(
          {
            background: appearance?.backgroundColor || '#FFFFFF',
            color: appearance?.textColor || '#1E2A5A',
          },
          appearance?.borderColor || '#EFEFEF',
        ),
        nonEditable: type !== 'actions',
      };

      if (type === 'actions') {
        return { ...cell, isEdit, newId: item?.actions?.['newId'] };
      }

      cell.nonEditable = !isEdit || shouldHideDependent || isRuleProtected;

      if (type === 'boolean') {
        if (shouldHideValue) {
          return {
            ...cell,
            type: 'dropdown',
            selectedValue: '',
            values: [
              { label: 'Да', value: 'true' },
              { label: 'Нет', value: 'false' },
            ],
            selectOpen: item?.selectOpen,
            selectKey: head?.columnName,
            styles: { indicatorSeparator: { display: 'none' } },
          };
        }

        const normalizedValue =
          value === true || value === 'true' || value === 1 || value === '1'
            ? 'true'
            : value === false || value === 'false' || value === 0 || value === '0'
              ? 'false'
              : '';

        return {
          ...cell,
          type: 'dropdown',
          selectedValue: normalizedValue,
          values: [
            { label: 'Да', value: 'true' },
            { label: 'Нет', value: 'false' },
          ],
          selectOpen: item?.selectOpen,
          selectKey: head?.columnName,
          styles: { indicatorSeparator: { display: 'none' } },
        };
      }

      if (type === 'number' || type === 'floatNumber') {
        return {
          ...cell,
          value: shouldHideValue ? null : value,
          ...(name.toLowerCase() === 'id' && { nonEditable: true }),
        };
      }

      if (type === 'date') {
        return {
          ...cell,
          date: shouldHideValue ? null : value,
          dateOpen: item?.dateOpen,
          dateKey: head?.columnName,
        };
      }

      if (type === 'dropdown') {
        return {
          ...cell,
          selectedValue: shouldHideValue ? '' : value?.toString() || '',
          values:
            head?.data?.map(el => ({
              label: el?.value,
              value: el?.key?.toString(),
            })) || [],
          selectOpen: item?.selectOpen,
          selectKey: head?.columnName,
          styles: { indicatorSeparator: { display: 'none' } },
        };
      }

      return { ...cell, text: shouldHideValue ? '' : value || '' };
    }),
    height: cellHeight,
  }));

  const totalsRow = buildTotalsRow(list, header, viewSettings, appearance);
  if (!totalsRow) {
    return [headerRow, ...bodyRows];
  }

  return viewSettings?.totalsRow?.position === 'start'
    ? [headerRow, totalsRow, ...bodyRows]
    : [headerRow, ...bodyRows, totalsRow];
};

interface MyFloatNumberCell extends Omit<NumberCell, 'type'> {
  type: 'floatNumber';
}

export const applyChanges = (
  changes: CellChange<
    | TextCell
    | MyDateCell
    | DropdownCell
    | MyHeaderCell
    | MyActionsCell
    | NumberCell
    | MyFloatNumberCell
  >[],
  prev: ListItem[],
  setSort: (sort: SortItem) => void,
  setHeaderRows: Dispatch<SetStateAction<Row<MyHeaderCell>>>,
): ListItem[] => {
  changes.forEach(change => {
    const { rowId, columnId } = change;
    const type = change.newCell.type;
    const newId = prev?.[rowId]?.['actions']?.['newId'];

    if (type === 'actions') {
      prev[rowId].actions.isEdit = change.newCell.isEdit;

      if (change.newCell.isRemoved) {
        if (newId) {
          prev = prev.filter(el => el.actions?.['newId'] !== newId);
        } else {
          prev = prev.filter(el => el.id !== prev?.[rowId]?.id);
        }
      }
    }

    if (type === 'header') {
      setHeaderRows(temp => {
        const sort = change.newCell?.['sort'];
        temp.cells.forEach((el, i) => {
          if (sort) {
            const isActive = el?.['text'] === change.newCell?.['text'];

            if (isActive) {
              setSort({ name: columnId.toString(), sort });
            }
            temp.cells[i].sort = isActive ? sort : undefined;
          }
        });

        return { ...temp };
      });
    }

    if (type === 'text') {
      prev[rowId][columnId] = change.newCell.text;
    }

    if (type === 'number' || type === 'floatNumber') {
      prev[rowId][columnId] = change.newCell.value;
    }

    if (type === 'date') {
      if (change.newCell.date !== change.previousCell?.['date']) {
        prev[rowId][columnId] = change.newCell.date;
      }
      prev[rowId].dateOpen = change.newCell.dateOpen;
    }

    if (type === 'dropdown') {
      if (
        change.newCell.selectedValue &&
        change.newCell.selectedValue !== change.previousCell?.['selectedValue']
      ) {
        prev[rowId][columnId] = change.newCell.selectedValue;
      }
      prev[rowId].selectOpen = change.newCell.selectOpen;
    }
  });

  return [...prev];
};
